if (!customElements.get('b2b-partner-form')) {
  customElements.define('b2b-partner-form', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.status = this.querySelector('[data-status]');
      this.storageKey = `b2b-application-request:${location.pathname}`;
      if (this.querySelector('[data-server-success]')) {
        try { sessionStorage.removeItem(this.storageKey); } catch (_) { /* Storage is optional. */ }
        this.querySelector('[data-server-success]').focus({ preventScroll: true });
        return;
      }
      this.button = this.form.querySelector('[type="submit"]');
      let requestId;
      try { requestId = sessionStorage.getItem(this.storageKey); } catch (_) { /* Storage is optional. */ }
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(requestId || '')) requestId = crypto.randomUUID();
      try { sessionStorage.setItem(this.storageKey, requestId); } catch (_) { /* Storage is optional. */ }
      this.querySelector('[data-request-id]').value = requestId;
      this.fields = Array.from(this.form.querySelectorAll('[data-field] input, [data-field] select, [data-field] textarea'));
      this.form.addEventListener('submit', event => this.saveApplication(event), { ...options, capture: true });
      this.form.addEventListener('input', event => {
        this.status.hidden = true;
        if (event.target.getAttribute('aria-invalid') === 'true') this.validate(event.target);
        this.updateCount();
      }, options);
      this.form.addEventListener('change', event => {
        this.status.hidden = true;
        if (this.fields.includes(event.target)) this.validate(event.target);
      }, options);
      this.form.addEventListener('focusout', event => {
        if (!this.fields.includes(event.target)) return;
        if (event.target.name === 'contact[website]') this.normalizeWebsite();
        if (event.target.value || event.target.getAttribute('aria-invalid') === 'true') this.validate(event.target);
      }, options);
      // JavaScript coordinates persistence before Shopify's native email POST.
      this.form.noValidate = true;
      this.button.disabled = false;
      this.querySelector('[data-server-status]')?.focus();
      this.updateCount();
    }

    async saveApplication(event) {
      this.normalizeWebsite();
      const invalid = this.fields.filter(field => !this.validate(field));
      if (invalid.length) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError('Please check the highlighted fields.');
        invalid[0].focus();
        return;
      }
      const values = new FormData(this.form);
      const payload = {};
      ['first_name', 'last_name', 'company', 'email', 'phone', 'country', 'state', 'business_channel', 'website', 'business_focus'].forEach(key => {
        payload[key] = values.get(`contact[${key}]`);
      });
      payload.requirements = values.get('contact[body]');
      payload.request_id = values.get('contact[Request ID]');
      payload.confirmation = values.get('application_confirmation') || '';
      const snapshot = JSON.stringify(payload);
      // Allow the native POST (and Shopify CAPTCHA) only for saved field values.
      if (this.savedSnapshot === snapshot) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.saving) return;
      this.saving = true;
      this.button.disabled = true;
      this.status.hidden = true;
      this.form.setAttribute('aria-busy', 'true');
      try {
        const endpoint = this.dataset.saveEndpoint;
        if (!endpoint || !/^\/apps\/[a-zA-Z0-9/_-]+$/.test(endpoint)) throw new Error('configuration');
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: snapshot,
          signal: AbortSignal.timeout(20000)
        });
        const result = await response.json();
        if (!response.ok || result.saved !== true) throw new Error('save');
        this.savedSnapshot = snapshot;
        this.saving = false;
        this.button.disabled = false;
        this.form.removeAttribute('aria-busy');
        // Changes made while saving are checked and persisted on this second event.
        this.form.requestSubmit(this.button);
      } catch (_) {
        this.saving = false;
        this.button.disabled = false;
        this.form.removeAttribute('aria-busy');
        this.showError('We couldn’t submit your application. Your details are still here—please try again in a moment.');
      }
    }

    showError(message) {
      this.status.hidden = false;
      this.status.classList.add('b2b-form__status--error');
      this.status.textContent = message;
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      if (this.form) this.form.noValidate = false;
    }

    normalizeWebsite() {
      const website = this.form.elements.namedItem('contact[website]');
      const value = website.value.trim();
      website.value = value && !/^[a-z][a-z\d+.-]*:/i.test(value) ? `https://${value}` : value;
    }

    validate(field) {
      const value = field.value.trim();
      let message = '';
      if (field.required && !value) message = 'Please complete this field.';
      else if (field.maxLength > 0 && value.length > field.maxLength) message = `Please use ${field.maxLength} characters or fewer.`;
      else if (value && field.type === 'email' && (field.validity.typeMismatch || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))) message = 'Please enter a valid email address.';
      else if (value && field.type === 'tel' && (!/^[+\d\s().x#-]+$/i.test(value) || value.replace(/\D/g, '').length < 7 || value.replace(/\D/g, '').length > 20)) message = 'Please enter a valid phone number.';
      else if (value && field.type === 'url') {
        try {
          const url = new URL(value);
          if (!['http:', 'https:'].includes(url.protocol) || !url.hostname.includes('.') || url.hostname.startsWith('.') || url.hostname.endsWith('.') || url.username || url.password) throw new Error('Invalid website');
        } catch (_) { message = 'Please enter a valid website, such as https://example.com.'; }
      }
      const error = field.closest('[data-field]').querySelector('.b2b-form__error');
      error.textContent = message;
      error.hidden = !message;
      field.setAttribute('aria-invalid', message ? 'true' : 'false');
      return !message;
    }

    updateCount() {
      const requirements = this.form.elements.namedItem('contact[body]');
      this.querySelector('[data-character-count]').textContent = `${requirements.value.length} / ${requirements.maxLength}`;
    }
  });
}
